<template>
  <component :is="isComponent ? 'div' : 'UPage'">
    <ConfigHeader
      v-if="!isComponent"
      :back-to="`/customers/${customerNo}/tenants`"
      :last-editor="config.lastEditor"
      :last-update="config.lastUpdate"
      :saving="saving"
      :tenant-name="tenantName"
      title="microtech Konfig"
      @save="saveConfig"
    />

    <component :is="isComponent ? 'div' : 'UPageBody'" :class="{ 'p-0': isComponent }">
      <div v-if="loading" class="flex justify-center py-6">
        <UIcon class="w-8 h-8 animate-spin text-muted" name="i-lucide-loader-circle" />
      </div>

      <div v-else class="space-y-6">
        <!-- Status & Activation -->
        <ConfigStatusCard
          :model-value="config.active"
          :disabled="!canWrite"
          title="microtech Automatisierung"
          description="Aktivieren oder deaktivieren Sie die microtech-Anbindung für diesen Mandanten."
          icon="i-lucide-database"
          @update:model-value="onActiveToggle"
        />

        <USeparator class="my-8" />

        <ConfigChangelogAlert
          description="Bitte stellen Sie sicher, dass Ihre microtech-Instanz die aktuellen Systemanforderungen erfüllt."
          release-type="microtech"
        />

        <!-- Einrichtungs-Informationen -->
        <UCard
          :ui="{
            root: isInfoOpen ? '' : 'divide-y-0',
            body: isInfoOpen ? 'p-6' : 'hidden',
            header: 'px-4 py-3 sm:px-6',
          }"
        >
          <template #header>
            <div
              class="flex items-center justify-between cursor-pointer"
              @click="isInfoOpen = !isInfoOpen"
            >
              <div class="flex items-center gap-3">
                <UIcon name="i-lucide-info" class="h-5 w-5 text-primary" />
                <h3 class="text-base font-semibold">Einrichtung und Verbindung testen</h3>
              </div>
              <UButton
                :icon="isInfoOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
                color="neutral"
                variant="ghost"
                @click.stop="isInfoOpen = !isInfoOpen"
              />
            </div>
          </template>

          <div v-show="isInfoOpen" class="space-y-6">

            <ConfigDocsAlert
              title="Einrichtung der microtech Komponente"
              description="Folgen Sie bitte den Anweisungen in unserer detaillierten Konfigurations-Dokumentation, um Ihre microtech-Instanz erfolgreich anzubinden."
              doc-key="general-config.microtech-connection"
              tenant-doc-key="tenant-config.microtech-portal-config"
            />

            <UAlert
              icon="i-lucide-lightbulb"
              title="Aktivierung"
              description="Es wird empfohlen, die Komponente erst nach einem erfolgreichen Verbindungstest zu aktivieren."
              color="warning"
              variant="subtle"
              :ui="{
                title: 'text-warning-900 dark:text-warning-200',
                description: 'text-warning-800 dark:text-warning-300',
              }"
            />
          </div>
        </UCard>

        <!-- Zugang -->
        <UCard
          :ui="{
            root: isAccessOpen ? '' : 'divide-y-0',
            body: isAccessOpen ? 'p-6' : 'hidden',
            header: 'px-4 py-3 sm:px-6',
          }"
        >
          <template #header>
            <div
              class="flex items-center justify-between cursor-pointer"
              @click="isAccessOpen = !isAccessOpen"
            >
              <div class="flex items-center gap-3">
                <UIcon name="i-lucide-key" class="h-5 w-5 text-primary" />
                <h3 class="text-base font-semibold">Zugang</h3>
              </div>
              <UButton
                :icon="isAccessOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
                color="neutral"
                variant="ghost"
                @click.stop="isAccessOpen = !isAccessOpen"
              />
            </div>
          </template>

          <div v-show="isAccessOpen" class="space-y-6">
            <UAlert
              v-if="isCentrallyManaged"
              icon="i-lucide-building-2"
              title="Verbindung wird zentral verwaltet"
              :description="centralManagementHint"
              color="info"
              variant="subtle"
            />

            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <UFormField label="Client ID">
                <UInput v-model="config.clientId" :disabled="connectionFieldsDisabled" class="w-full" placeholder="••••••••" />
              </UFormField>
              <UFormField label="Client Secret">
                <UInput
                  v-model="config.clientSecret"
                  :disabled="connectionFieldsDisabled"
                  class="w-full"
                  placeholder="••••••••"
                  type="password"
                />
              </UFormField>
              <UFormField label="GraphQL URL">
                <template #label>
                  <div class="flex items-center gap-1">
                    GraphQL URL
                    <UTooltip text="https://<server.domain>/microtech/erp/mand/<MandNr>/">
                      <UIcon class="w-4 h-4 text-gray-400" name="i-lucide-info" />
                    </UTooltip>
                  </div>
                </template>
                <UInput
                  v-model="config.graphUrl"
                  :disabled="connectionFieldsDisabled"
                  class="w-full"
                  placeholder="https://demo.de/microtech/erp/0EC0../"
                />
              </UFormField>
            </div>

            <div v-if="isNetbirdUrl">
              <UFormField
                label="TLS-Zertifikat (PEM)"
                description="Optionales PEM-Zertifikat für verschlüsselte Verbindungen zu privaten NetBird-Endpunkten (*.netbird.cloud)."
              >
                <UTextarea
                  v-model="config.certificate"
                  :disabled="connectionFieldsDisabled"
                  class="w-full font-mono text-xs"
                  :rows="6"
                  placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                />
              </UFormField>
            </div>
          </div>
        </UCard>

        <!-- Verbindungstest -->
        <UCard
          :ui="{
            root: isTestOpen ? '' : 'divide-y-0',
            body: isTestOpen ? 'p-6' : 'hidden',
            header: 'px-4 py-3 sm:px-6',
          }"
        >
          <template #header>
            <div
              class="flex items-center justify-between cursor-pointer"
              @click="isTestOpen = !isTestOpen"
            >
              <div class="flex items-center gap-3">
                <UIcon name="i-lucide-zap" class="h-5 w-5 text-primary" />
                <h3 class="text-base font-semibold">Verbindungstest</h3>
              </div>
              <UButton
                :icon="isTestOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
                color="neutral"
                variant="ghost"
                @click.stop="isTestOpen = !isTestOpen"
              />
            </div>
          </template>

          <div v-show="isTestOpen" class="space-y-6">
            <div
              class="flex flex-col sm:flex-row sm:items-center gap-6 p-4 rounded-lg border border-primary-200 bg-primary-50/50 dark:border-primary-800 dark:bg-primary-900/20"
            >
              <div class="flex-1">
                <h4 class="font-bold">microtech Verbindungstest</h4>
                <p class="text-sm text-muted mt-1">
                  Überprüfen Sie Ihre Zugangsdaten, bevor Sie die Konfiguration aktivieren. Bei
                  diesem Test werden keine Daten gespeichert.
                </p>
              </div>
              <UButton
                v-if="canWrite"
                :loading="testingCredentials"
                color="primary"
                icon="i-lucide-zap"
                label="Zugang testen"
                @click="testCredentials"
              />
            </div>

            <div
              v-if="testResult"
              class="rounded-lg border p-4 transition-all duration-300"
              :class="
                testResult.success
                  ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                  : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
              "
            >
              <div class="flex items-center gap-3 mb-6">
                <UIcon
                  :name="testResult.success ? 'i-lucide-check-circle' : 'i-lucide-x-circle'"
                  :class="testResult.success ? 'text-green-500' : 'text-red-500'"
                  class="w-8 h-8"
                />
                <span
                  :class="
                    testResult.success
                      ? 'text-green-700 dark:text-green-300'
                      : 'text-red-700 dark:text-red-300'
                  "
                  class="font-bold text-lg"
                >
                  {{ testResult.message }}
                </span>
              </div>

              <!-- Detaillierte Testergebnisse -->
              <div
                v-if="testResult.checks"
                class="grid gap-4 sm:grid-cols-2 mt-6 border-t border-gray-200 dark:border-gray-800 pt-6"
              >
                <div
                  v-for="check in testResult.checks.filter((c) =>
                    ['Token-Abruf', 'GraphQL-Verbindung'].includes(c.label),
                  )"
                  :key="check.label"
                  class="flex gap-4 p-4 rounded-lg bg-white/50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-800"
                >
                  <div class="mt-0.5 shrink-0">
                    <UIcon
                      :name="
                        check.status === 'success' ? 'i-lucide-check-circle' : 'i-lucide-x-circle'
                      "
                      :class="check.status === 'success' ? 'text-green-500' : 'text-red-500'"
                      class="w-5 h-5 flex-shrink-0"
                    />
                  </div>
                  <div>
                    <p class="font-bold text-gray-900 dark:text-white text-sm">{{ check.label }}</p>
                    <p class="text-gray-600 dark:text-gray-400 text-xs mt-1 leading-relaxed">
                      {{ check.detail }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </UCard>

        <!-- Suche & Indexierung -->
        <UCard
          v-if="config.active"
          :ui="{
            root: isSearchOpen ? '' : 'divide-y-0',
            body: isSearchOpen ? 'p-6' : 'hidden',
            header: 'px-4 py-3 sm:px-6',
          }"
        >
          <template #header>
            <div
              class="flex items-center justify-between cursor-pointer"
              @click="isSearchOpen = !isSearchOpen"
            >
              <div class="flex items-center gap-3">
                <UIcon name="i-lucide-search" class="h-5 w-5 text-primary" />
                <h3 class="text-base font-semibold">Suche & Indexierung</h3>
              </div>
              <div class="flex items-center gap-3">
                <UBadge
                  :color="config.indexingEnabled ? 'success' : 'neutral'"
                  size="sm"
                  variant="subtle"
                >
                  {{ config.indexingEnabled ? 'Aktiv' : 'Inaktiv' }}
                </UBadge>
                <UButton
                  :icon="isSearchOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
                  color="neutral"
                  variant="ghost"
                  @click.stop="isSearchOpen = !isSearchOpen"
                />
              </div>
            </div>
          </template>

          <div v-show="isSearchOpen">
            <div class="flex items-center justify-between gap-4">
              <div class="flex items-center gap-2">
                  <p class="text-sm text-muted cursor-help">
                    Aktivieren Sie die Indexierung Ihrer microtech-Daten, damit Portal-Nutzer diese über die Suche finden können. Wenn Sie keine Portal-Nutzer haben, deaktivieren Sie die Option, da die Indexierung dann keinen Nutzen bietet.
                  </p>
              </div>
              <USwitch v-model="config.indexingEnabled" :disabled="!canWrite" />
            </div>
          </div>
        </UCard>

        <!-- USt-IdNr.-Prüfung (Bundeszentralamt für Steuern) -->
        <UCard
          v-if="config.active && config.vatValidation"
          :ui="{
            root: isVatValidationOpen ? '' : 'divide-y-0',
            body: isVatValidationOpen ? 'p-6' : 'hidden',
            header: 'px-4 py-3 sm:px-6',
          }"
        >
          <template #header>
            <div
              class="flex items-center justify-between cursor-pointer"
              @click="isVatValidationOpen = !isVatValidationOpen"
            >
              <div class="flex items-center gap-3">
                <UIcon name="i-lucide-shield-check" class="h-5 w-5 text-primary" />
                <h3 class="text-base font-semibold">USt-IdNr.-Prüfung (Bundeszentralamt für Steuern)</h3>
              </div>
              <div class="flex items-center gap-3">
                <UBadge
                  :color="config.vatValidation.active ? 'success' : 'neutral'"
                  size="sm"
                  variant="subtle"
                >
                  {{ config.vatValidation.active ? 'Aktiv' : 'Inaktiv' }}
                </UBadge>
                <UButton
                  :icon="isVatValidationOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
                  color="neutral"
                  variant="ghost"
                  @click.stop="isVatValidationOpen = !isVatValidationOpen"
                />
              </div>
            </div>
          </template>

          <div v-show="isVatValidationOpen" class="space-y-6">
            <!-- Globale Aktivierung -->
            <div class="flex items-center justify-between gap-4">
              <div class="space-y-0.5">
                <p class="text-sm font-medium text-gray-900 dark:text-white">
                  USt-IdNr.-Prüfung aktivieren
                </p>
                <p class="text-sm text-muted">
                  Aktiviert das offizielle Bestätigungsverfahren des Bundeszentralamts für Steuern (BZSt) für EU-USt-IdNrn. und die formale Prüfung deutscher USt-IdNrn.
                </p>
              </div>
              <USwitch
                v-model="config.vatValidation.active"
                :disabled="!canWrite"
                @update:model-value="emit('dirty')"
              />
            </div>

            <USeparator />

            <!-- Prüfungsumfang -->
            <div class="space-y-4">
              <h4 class="text-sm font-semibold uppercase tracking-wider text-muted">
                Prüfungsumfang
              </h4>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="flex items-start justify-between gap-4 p-4 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/20">
                  <div class="space-y-1">
                    <p class="text-sm font-medium text-gray-900 dark:text-white">
                      Ausländische EU-USt-IdNrn. amtlich prüfen
                    </p>
                    <p class="text-xs text-muted leading-relaxed">
                      Führt eine qualifizierte Bestätigung über die offizielle Schnittstelle des Bundeszentralamts für Steuern (BZSt) durch.
                    </p>
                  </div>
                  <USwitch
                    v-model="config.vatValidation.checkForeignEuVatIds"
                    :disabled="!canWrite"
                    class="shrink-0 pt-0.5"
                    @update:model-value="emit('dirty')"
                  />
                </div>

                <div class="flex items-start justify-between gap-4 p-4 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/20">
                  <div class="space-y-1">
                    <p class="text-sm font-medium text-gray-900 dark:text-white">
                      Deutsche USt-IdNrn. formal validieren
                    </p>
                    <p class="text-xs text-muted leading-relaxed">
                      Validiert das Format deutscher USt-IdNrn. nach dem Muster DE gefolgt von 9 Ziffern (^DE\d{9}$ bzw. (DE)[0-9]{9}). Wird als formale Validierung gekennzeichnet (keine Bestätigung durch das Bundeszentralamt für Steuern).
                    </p>
                  </div>
                  <USwitch
                    v-model="config.vatValidation.validateGermanVatIdsFormally"
                    :disabled="!canWrite"
                    class="shrink-0 pt-0.5"
                    @update:model-value="emit('dirty')"
                  />
                </div>
              </div>
            </div>

            <USeparator />

            <!-- Adressprüfung -->
            <div class="space-y-6">
              <div>
                <h4 class="text-sm font-semibold uppercase tracking-wider text-muted">
                  Adressprüfung
                </h4>
                <p class="text-xs text-muted mt-1 leading-relaxed">
                  Steuerung von automatischen Prüfungen, Sperrungen und manuellen Stapelläufen für Kunden- und Lieferantenadressen.
                </p>
              </div>

              <!-- Auslöser & Intervalle -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="flex items-start justify-between gap-4 p-4 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/20">
                  <div class="space-y-1">
                    <p class="text-sm font-medium text-gray-900 dark:text-white">
                      Prüfung bei Neuanlage
                    </p>
                    <p class="text-xs text-muted leading-relaxed">
                      Prüft die USt-IdNr. automatisch, sobald eine neue Adresse mit USt-IdNr. in microtech angelegt wird.
                    </p>
                  </div>
                  <USwitch
                    v-model="config.vatValidation.checkOnAddressCreate"
                    :disabled="!canWrite"
                    class="shrink-0 pt-0.5"
                    @update:model-value="emit('dirty')"
                  />
                </div>

                <div class="flex items-start justify-between gap-4 p-4 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/20">
                  <div class="space-y-1">
                    <p class="text-sm font-medium text-gray-900 dark:text-white">
                      Prüfung bei Änderung prüfrelevanter Felder
                    </p>
                    <p class="text-xs text-muted leading-relaxed">
                      Löst eine erneute Prüfung aus, wenn sich Firmenname, Straße, PLZ, Ort oder USt-IdNr. ändern.
                    </p>
                  </div>
                  <USwitch
                    v-model="config.vatValidation.checkOnAddressUpdate"
                    :disabled="!canWrite"
                    class="shrink-0 pt-0.5"
                    @update:model-value="emit('dirty')"
                  />
                </div>

                <div class="flex items-start justify-between gap-4 p-4 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/20">
                  <div class="space-y-1">
                    <p class="text-sm font-medium text-gray-900 dark:text-white">
                      Regelmäßige Wiederholungsprüfung
                    </p>
                    <p class="text-xs text-muted leading-relaxed">
                      Führt automatische Wiederholungsprüfungen von Bestandsadressen in regelmäßigen Abständen durch.
                    </p>
                  </div>
                  <USwitch
                    v-model="config.vatValidation.periodicCheckEnabled"
                    :disabled="!canWrite"
                    class="shrink-0 pt-0.5"
                    @update:model-value="emit('dirty')"
                  />
                </div>

                <div
                  class="flex items-start justify-between gap-4 p-4 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/20"
                  :class="{ 'opacity-60': !config.vatValidation.periodicCheckEnabled }"
                >
                  <div class="space-y-1 pr-2">
                    <p class="text-sm font-medium text-gray-900 dark:text-white">
                      Wiederholungsintervall
                    </p>
                    <p class="text-xs text-muted leading-relaxed">
                      Dient der Sicherstellung eines aktuellen Prüfstatus (in Tagen).
                    </p>
                  </div>
                  <div class="w-28 shrink-0">
                    <UInput
                      v-model.number="config.vatValidation.periodicCheckIntervalDays"
                      type="number"
                      min="1"
                      max="365"
                      :disabled="!canWrite || !config.vatValidation.periodicCheckEnabled"
                      placeholder="30"
                      class="w-full"
                      @update:model-value="emit('dirty')"
                    />
                  </div>
                </div>
              </div>

              <!-- Automatische Adresssperrung (Teil der Adressprüfung) -->
              <div class="space-y-3 pt-2">
                <div class="space-y-0.5">
                  <h5 class="text-xs font-semibold uppercase tracking-wider text-muted">
                    Automatische Adresssperrung
                  </h5>
                  <p class="text-xs text-muted leading-relaxed">
                    Bestimmt für inländische und ausländische Adressen, wann die Adresse bei negativem Prüfergebnis in microtech automatisch gesperrt werden soll (fldGspKz), und wer darüber per E-Mail informiert wird.
                  </p>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <!-- Inländische Adressen (Deutschland) -->
                  <div class="p-4 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/20 space-y-4">
                    <div class="border-b border-gray-200 dark:border-gray-700/60 pb-2">
                      <p class="text-sm font-semibold text-gray-900 dark:text-white">
                        Inländische Adressen (Deutschland)
                      </p>
                      <p class="text-xs text-muted">
                        Formale Regex-Prüfung nach ^DE\d{9}$
                      </p>
                    </div>

                    <div class="space-y-3">
                      <div class="flex items-start justify-between gap-4">
                        <div class="space-y-0.5">
                          <p class="text-sm font-medium text-gray-900 dark:text-white">
                            Fehlende USt-IdNr.
                          </p>
                          <p class="text-xs text-muted leading-relaxed">
                            Adresse sperren, wenn keine USt-IdNr. hinterlegt ist.
                          </p>
                        </div>
                        <UCheckbox
                          v-model="config.vatValidation.lockDomesticMissingVatId"
                          :disabled="!canWrite"
                          class="shrink-0 pt-0.5"
                          @update:model-value="emit('dirty')"
                        />
                      </div>

                      <div class="flex items-start justify-between gap-4 pt-2 border-t border-gray-200/60 dark:border-gray-800">
                        <div class="space-y-0.5">
                          <p class="text-sm font-medium text-gray-900 dark:text-white">
                            Falsche USt-IdNr. (formal inkorrekt)
                          </p>
                          <p class="text-xs text-muted leading-relaxed">
                            Adresse sperren, wenn das Format nicht dem Muster ^DE\d{9}$ entspricht.
                          </p>
                        </div>
                        <UCheckbox
                          v-model="config.vatValidation.lockDomesticInvalidVatId"
                          :disabled="!canWrite"
                          class="shrink-0 pt-0.5"
                          @update:model-value="emit('dirty')"
                        />
                      </div>
                    </div>

                    <VatLockNotificationSettings
                      v-model="config.vatValidation.domesticLockNotification"
                      class="pt-3 border-t border-gray-200 dark:border-gray-700/60"
                      label="E-Mail bei Sperrung"
                      description="E-Mail, wenn eine deutsche Adresse bei einer Adressprüfung gesperrt wird. Stapel- und Wiederholungsprüfungen senden einen Bericht pro Lauf."
                      :disabled="!canWrite"
                      data-test="notify-domestic"
                      @dirty="emit('dirty')"
                    />
                  </div>

                  <!-- Ausländische Adressen (EU-Ausland) -->
                  <div class="p-4 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/20 space-y-4">
                    <div class="border-b border-gray-200 dark:border-gray-700/60 pb-2">
                      <p class="text-sm font-semibold text-gray-900 dark:text-white">
                        Ausländische Adressen (EU-Ausland)
                      </p>
                      <p class="text-xs text-muted">
                        Amtliche Prüfung über das Bundeszentralamt für Steuern (BZSt)
                      </p>
                    </div>

                    <div class="space-y-3">
                      <div class="flex items-start justify-between gap-4">
                        <div class="space-y-0.5">
                          <p class="text-sm font-medium text-gray-900 dark:text-white">
                            Fehlende USt-IdNr.
                          </p>
                          <p class="text-xs text-muted leading-relaxed">
                            Adresse sperren, wenn keine USt-IdNr. hinterlegt ist.
                          </p>
                        </div>
                        <UCheckbox
                          v-model="config.vatValidation.lockForeignMissingVatId"
                          :disabled="!canWrite"
                          class="shrink-0 pt-0.5"
                          @update:model-value="emit('dirty')"
                        />
                      </div>

                      <div class="flex items-start justify-between gap-4 pt-2 border-t border-gray-200/60 dark:border-gray-800">
                        <div class="space-y-0.5">
                          <p class="text-sm font-medium text-gray-900 dark:text-white">
                            USt-IdNr. ungültig
                          </p>
                          <p class="text-xs text-muted leading-relaxed">
                            Adresse sperren, wenn die BZSt-Prüfung fehlschlägt oder die USt-IdNr. ungültig ist.
                          </p>
                        </div>
                        <UCheckbox
                          v-model="config.vatValidation.lockForeignInvalidVatId"
                          :disabled="!canWrite"
                          class="shrink-0 pt-0.5"
                          @update:model-value="emit('dirty')"
                        />
                      </div>

                      <div class="flex items-start justify-between gap-4 pt-2 border-t border-gray-200/60 dark:border-gray-800">
                        <div class="space-y-0.5">
                          <p class="text-sm font-medium text-gray-900 dark:text-white">
                            USt-IdNr. gültig + Adressangaben nicht korrekt
                          </p>
                          <p class="text-xs text-muted leading-relaxed">
                            Adresse sperren, wenn die USt-IdNr. zwar gültig ist, aber Abweichungen bei der Anschrift vorliegen (eingeschränkt gültig).
                          </p>
                        </div>
                        <UCheckbox
                          v-model="config.vatValidation.lockForeignAddressMismatch"
                          :disabled="!canWrite"
                          class="shrink-0 pt-0.5"
                          @update:model-value="emit('dirty')"
                        />
                      </div>
                    </div>

                    <VatLockNotificationSettings
                      v-model="config.vatValidation.foreignLockNotification"
                      class="pt-3 border-t border-gray-200 dark:border-gray-700/60"
                      label="E-Mail bei Sperrung"
                      description="E-Mail, wenn eine Adresse im EU-Ausland bei einer Adressprüfung gesperrt wird. Stapel- und Wiederholungsprüfungen senden einen Bericht pro Lauf."
                      :disabled="!canWrite"
                      data-test="notify-foreign"
                      @dirty="emit('dirty')"
                    />
                  </div>
                </div>
              </div>

              <!-- Manuelle Stapelprüfung (Teil der Adressprüfung) -->
              <div class="space-y-3 pt-2">
                <div class="space-y-0.5">
                  <h5 class="text-xs font-semibold uppercase tracking-wider text-muted">
                    Manuelle Stapelprüfung
                  </h5>
                  <p class="text-xs text-muted leading-relaxed">
                    Führt eine sofortige Prüfung von Bestandsadressen durch. Sie können die Stapelprüfung gezielt für inländische oder ausländische Adressen anstoßen.
                  </p>
                </div>

                <div class="p-4 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/20">
                  <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div class="space-y-1">
                      <p class="text-sm font-medium text-gray-900 dark:text-white">
                        Stapelprüfung jetzt ausführen
                      </p>
                      <p class="text-xs text-muted leading-relaxed">
                        Inland: Formale Prüfung (^DE\d{9}$). Ausland: Qualifizierte BZSt-Prüfung über die amtliche Schnittstelle.
                      </p>
                    </div>
                    <div class="flex flex-wrap items-center gap-2 shrink-0">
                      <UButton
                        icon="i-lucide-play"
                        label="Inländische Adressen prüfen"
                        color="neutral"
                        variant="outline"
                        :loading="batchValidationRunning && batchValidationScope === 'domestic'"
                        :disabled="!canWrite || !config.vatValidation.active || batchValidationRunning"
                        @click="onStartBatchValidation('domestic')"
                      />
                      <UButton
                        icon="i-lucide-play"
                        label="Ausländische Adressen prüfen"
                        color="primary"
                        variant="soft"
                        :loading="batchValidationRunning && batchValidationScope === 'foreign'"
                        :disabled="!canWrite || !config.vatValidation.active || batchValidationRunning"
                        @click="onStartBatchValidation('foreign')"
                      />
                      <UButton
                        icon="i-lucide-play"
                        label="Alle prüfen"
                        color="neutral"
                        variant="ghost"
                        :loading="batchValidationRunning && batchValidationScope === 'all'"
                        :disabled="!canWrite || !config.vatValidation.active || batchValidationRunning"
                        @click="onStartBatchValidation('all')"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <USeparator />

            <!-- Vorgangsprüfung -->
            <div class="space-y-4">
              <h4 class="text-sm font-semibold uppercase tracking-wider text-muted">
                Vorgangsprüfung
              </h4>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="flex items-start justify-between gap-4 p-4 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/20">
                  <div class="space-y-1">
                    <p class="text-sm font-medium text-gray-900 dark:text-white">
                      Prüfung bei Vorgangs-Neuanlage
                    </p>
                    <p class="text-xs text-muted leading-relaxed">
                      Löst bei Erstellung eines konfigurierten Belegs eine automatische USt-Prüfung aus.
                    </p>
                  </div>
                  <USwitch
                    v-model="config.vatValidation.checkOnTransactionCreate"
                    :disabled="!canWrite"
                    class="shrink-0 pt-0.5"
                    @update:model-value="onTransactionTriggerChange"
                  />
                </div>

                <div class="flex items-start justify-between gap-4 p-4 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/20">
                  <div class="space-y-1">
                    <p class="text-sm font-medium text-gray-900 dark:text-white">
                      Prüfung bei Vorgangs-Änderung
                    </p>
                    <p class="text-xs text-muted leading-relaxed">
                      Löst bei Speicherung geänderter Belege eine Prüfung der USt-IdNr. aus.
                    </p>
                  </div>
                  <USwitch
                    v-model="config.vatValidation.checkOnTransactionUpdate"
                    :disabled="!canWrite"
                    class="shrink-0 pt-0.5"
                    @update:model-value="onTransactionTriggerChange"
                  />
                </div>
              </div>

              <div
                class="grid grid-cols-1 md:grid-cols-2 gap-4"
                :class="{ 'opacity-60': !isTransactionCheckActive }"
              >
                <div class="p-4 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/20 flex flex-col justify-between">
                  <UFormField
                    label="Zu prüfende Vorgangsarten"
                    :error="transactionTypesError"
                  >
                    <USelectMenu
                      v-model="config.vatValidation.transactionTypes"
                      :items="transactionTypeOptions"
                      :disabled="!canWrite || !isTransactionCheckActive"
                      :loading="loadingTransactionTypes"
                      multiple
                      placeholder="Vorgangsarten auswählen..."
                      class="w-full mt-1.5"
                      @update:model-value="emit('dirty')"
                    />
                  </UFormField>
                </div>

                <div class="p-4 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/20 flex items-start justify-between gap-4">
                  <div class="space-y-1 pr-2">
                    <p class="text-sm font-medium text-gray-900 dark:text-white">
                      Wiederverwendungs-Zeitfenster
                    </p>
                    <p class="text-xs text-muted leading-relaxed">
                      Vorhandene erfolgreiche Prüfungen mit identischen Daten innerhalb dieses Zeitraums (in Tagen) wiederverwenden.
                    </p>
                  </div>
                  <div class="w-28 shrink-0">
                    <UInput
                      v-model.number="config.vatValidation.reuseWindowDays"
                      type="number"
                      min="0"
                      max="30"
                      :disabled="!canWrite || !isTransactionCheckActive"
                      placeholder="Tage"
                      class="w-full"
                      @update:model-value="emit('dirty')"
                    />
                  </div>
                </div>
              </div>

              <!-- Sperrung und Benachrichtigung bei Vorgängen -->
              <div class="space-y-3 pt-2">
                <div class="space-y-0.5">
                  <h5 class="text-xs font-semibold uppercase tracking-wider text-muted">
                    Sperrung bei Vorgängen
                  </h5>
                  <p class="text-xs text-muted leading-relaxed">
                    Legt fest, was gesperrt wird, wenn die USt-IdNr. bei der Prüfung eines Vorgangs durchfällt. Ob gesperrt wird, bestimmen die Sperrgründe der Adressprüfung (oben).
                  </p>
                </div>

                <div
                  class="grid grid-cols-1 md:grid-cols-2 gap-4"
                  :class="{ 'opacity-60': !isTransactionCheckActive }"
                >
                  <div
                    v-for="scope in transactionLockScopes"
                    :key="scope.key"
                    class="p-4 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/20 space-y-4"
                    :data-test="`transaction-lock-${scope.key}`"
                  >
                    <div class="border-b border-gray-200 dark:border-gray-700/60 pb-2">
                      <p class="text-sm font-semibold text-gray-900 dark:text-white">
                        {{ scope.title }}
                      </p>
                      <p class="text-xs text-muted">
                        {{ scope.subtitle }}
                      </p>
                    </div>

                    <div class="space-y-3">
                      <div class="flex items-start justify-between gap-4">
                        <div class="space-y-0.5">
                          <p class="text-sm font-medium text-gray-900 dark:text-white">
                            Adresse sperren
                          </p>
                          <p class="text-xs text-muted leading-relaxed">
                            Sperrt die Adresse des Vorgangs.
                          </p>
                        </div>
                        <UCheckbox
                          :model-value="transactionLocksAddress(scope.key)"
                          :disabled="!canWrite || !isTransactionCheckActive"
                          class="shrink-0 pt-0.5"
                          @update:model-value="(value: boolean | 'indeterminate') => setTransactionLocksAddress(scope.key, value === true)"
                        />
                      </div>

                      <div class="flex items-start justify-between gap-4 pt-2 border-t border-gray-200/60 dark:border-gray-800">
                        <div class="space-y-0.5">
                          <p class="text-sm font-medium text-gray-900 dark:text-white">
                            Vorgang sperren
                          </p>
                          <p class="text-xs text-muted leading-relaxed">
                            Sperrt den geprüften Vorgang (z. B. Auftrag, Rechnung).
                          </p>
                        </div>
                        <UCheckbox
                          v-model="config.vatValidation[scope.lockTransactionField]"
                          :disabled="!canWrite || !isTransactionCheckActive"
                          class="shrink-0 pt-0.5"
                          @update:model-value="emit('dirty')"
                        />
                      </div>
                    </div>

                    <VatLockNotificationSettings
                      v-model="config.vatValidation[scope.notificationField]"
                      class="pt-3 border-t border-gray-200 dark:border-gray-700/60"
                      label="E-Mail bei Sperrung"
                      :description="scope.notificationDescription"
                      :disabled="!canWrite || !isTransactionCheckActive"
                      :data-test="`notify-transaction-${scope.key}`"
                      @dirty="emit('dirty')"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </UCard>
      </div>
    </component>

    <!-- Activation/Deactivation Confirmation Modal -->
    <ConfirmationModal
      v-model:open="isConfirmModalOpen"
      :description="
        config.active
          ? 'Bestätigen Sie die Deaktivierung der microtech-Komponente.'
          : 'Bestätigen Sie die Aktivierung der microtech-Komponente.'
      "
      :message="
        config.active
          ? 'Möchten Sie die microtech-Komponente wirklich deaktivieren?'
          : !testResult?.success
            ? 'Möchten Sie die microtech-Komponente wirklich aktivieren? ACHTUNG: Der Verbindungstest war nicht erfolgreich. Die Komponente funktioniert möglicherweise nicht korrekt.'
            : 'Möchten Sie die microtech-Komponente wirklich aktivieren?'
      "
      :title="config.active ? 'microtech deaktivieren' : 'microtech aktivieren'"
      @confirm="confirmToggle"
    />
    <ConfirmationModal
      v-model:open="isConfirmSaveModalOpen"
      title="Konfiguration speichern"
      description="Bestätigen Sie das Speichern der Konfiguration."
      message="ACHTUNG: Der Verbindungstest war nicht erfolgreich. Die Komponente funktioniert möglicherweise nicht korrekt. Möchten Sie die Konfiguration trotzdem speichern?"
      @confirm="confirmSave"
    />
  </component>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import axios from 'axios'
import ConfirmationModal from '@/components/ConfirmationModal.vue'
import { useNetbirdUrl } from '@/composables/useNetbirdUrl'
import { vatValidationApi } from '@/api/vatValidation'
import VatLockNotificationSettings from '@/components/vat/VatLockNotificationSettings.vue'
import {
  lockNotificationError,
  normalizeLockNotification,
  type VatLockNotification,
} from '@/utils/vatNotification'

export interface VatValidationConfig {
  active: boolean
  checkForeignEuVatIds: boolean
  validateGermanVatIdsFormally: boolean
  checkOnAddressCreate: boolean
  checkOnAddressUpdate: boolean
  periodicCheckEnabled: boolean
  periodicCheckIntervalDays: number
  lockDomesticMissingVatId: boolean
  lockDomesticInvalidVatId: boolean
  lockForeignMissingVatId: boolean
  lockForeignInvalidVatId: boolean
  lockForeignAddressMismatch: boolean
  domesticLockNotification: VatLockNotification
  foreignLockNotification: VatLockNotification
  // Transaction checks: what gets locked and who is notified, per address scope. A missing address flag means true.
  transactionLockDomesticAddress?: boolean
  transactionLockDomesticTransaction: boolean
  domesticTransactionLockNotification: VatLockNotification
  transactionLockForeignAddress?: boolean
  transactionLockForeignTransaction: boolean
  foreignTransactionLockNotification: VatLockNotification
  transactionCheckActive: boolean
  transactionTypes: string[]
  checkOnTransactionCreate: boolean
  checkOnTransactionUpdate: boolean
  reuseWindowDays: number
  validationType: string
}

interface MicrotechConfig {
  active: boolean
  indexingEnabled: boolean
  clientId: string
  clientSecret: string
  graphUrl: string
  certificate?: string
  vatValidation?: VatValidationConfig
  lastUpdate?: string
  lastEditor?: string
  source?: 'tenant' | 'customer'
  serverName?: string
  mandantNo?: string
}

interface TestCheck {
  label: string
  status: 'success' | 'warning' | 'error' | 'neutral'
  detail: string
}

interface TestResult {
  success: boolean
  message: string
  checks: TestCheck[]
}

const props = defineProps({
  customerNo: {
    type: String,
    required: true,
  },
  tenantName: {
    type: String,
    required: true,
  },
  isComponent: {
    type: Boolean,
    default: false,
  },
  canWrite: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['dirty'])

const toast = useToast()

const loading = ref(true)
const saving = ref(false)
const testingCredentials = ref(false)
const testResult = ref<{ success: boolean; message: string; checks?: TestCheck[] } | null>(null)

const defaultVatValidation: VatValidationConfig = {
  active: false,
  checkForeignEuVatIds: true,
  validateGermanVatIdsFormally: true,
  checkOnAddressCreate: true,
  checkOnAddressUpdate: true,
  periodicCheckEnabled: false,
  periodicCheckIntervalDays: 90,
  lockDomesticMissingVatId: false,
  lockDomesticInvalidVatId: false,
  lockForeignMissingVatId: false,
  lockForeignInvalidVatId: false,
  lockForeignAddressMismatch: false,
  domesticLockNotification: normalizeLockNotification(),
  foreignLockNotification: normalizeLockNotification(),
  transactionLockDomesticAddress: true,
  transactionLockDomesticTransaction: false,
  domesticTransactionLockNotification: normalizeLockNotification(),
  transactionLockForeignAddress: true,
  transactionLockForeignTransaction: false,
  foreignTransactionLockNotification: normalizeLockNotification(),
  transactionCheckActive: false,
  transactionTypes: [],
  checkOnTransactionCreate: false,
  checkOnTransactionUpdate: false,
  reuseWindowDays: 1,
  validationType: 'QUALIFIED',
}

const config = ref<MicrotechConfig>({
  active: false,
  indexingEnabled: false,
  clientId: '',
  clientSecret: '',
  graphUrl: '',
  certificate: '',
  vatValidation: { ...defaultVatValidation },
})

const { isNetbirdUrl } = useNetbirdUrl(() => config.value.graphUrl)

// Connection data of a centrally managed tenant lives on the customer level server
// and can only be edited under /customer-admin/microtech.
const isCentrallyManaged = computed(() => config.value.source === 'customer')
const connectionFieldsDisabled = computed(() => !props.canWrite || isCentrallyManaged.value)
const centralManagementHint = computed(() => {
  const server = config.value.serverName || 'unbekannt'
  const mandant = config.value.mandantNo || 'unbekannt'
  return `Verbindung wird zentral verwaltet: Server ${server}, Mandant ${mandant}. Zugangsdaten und URL können nur in der Kundenverwaltung geändert werden.`
})

const isInfoOpen = ref(false)
const isAccessOpen = ref(false)
const isTestOpen = ref(false)
const isSearchOpen = ref(false)
const isVatValidationOpen = ref(false)
const isConfirmModalOpen = ref(false)
const isConfirmSaveModalOpen = ref(false)

const loadingTransactionTypes = ref(false)
const availableTransactionTypes = ref<string[]>([])

const transactionTypeOptions = computed(() => {
  const set = new Set<string>()
  for (const t of availableTransactionTypes.value) {
    if (t?.trim()) set.add(t.trim())
  }
  for (const t of config.value.vatValidation?.transactionTypes || []) {
    if (t?.trim()) set.add(t.trim())
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'de'))
})

const fetchTransactionTypes = async () => {
  if (!config.value.active) return
  loadingTransactionTypes.value = true
  try {
    // Dedicated endpoint (settings read permission) instead of the GraphQL proxy, which
    // requires microtech data access that tenant admins do not have.
    const response = await axios.get<string[]>('/api/microtech/v1/transaction-types')
    if (Array.isArray(response.data)) {
      availableTransactionTypes.value = response.data.filter((t) => typeof t === 'string' && t.trim() !== '')
    }
  } catch (error) {
    console.error('Failed to fetch transaction types from microtech', error)
  } finally {
    loadingTransactionTypes.value = false
  }
}

watch(isVatValidationOpen, (open) => {
  if (open && config.value.active && availableTransactionTypes.value.length === 0) {
    fetchTransactionTypes()
  }
})

watch(
  () => config.value.active,
  (active) => {
    if (active && availableTransactionTypes.value.length === 0) {
      fetchTransactionTypes()
    }
  }
)

const isTransactionCheckActive = computed(() => {
  return Boolean(
    config.value.vatValidation?.checkOnTransactionCreate ||
    config.value.vatValidation?.checkOnTransactionUpdate
  )
})

const isTransactionTypesRequired = computed(() => {
  return Boolean(
    config.value.vatValidation?.active && isTransactionCheckActive.value
  )
})

const hasSelectedTransactionTypes = computed(() => {
  const types = config.value.vatValidation?.transactionTypes
  return Array.isArray(types) && types.some((t) => typeof t === 'string' && t.trim().length > 0)
})

const transactionTypesError = computed(() => {
  if (isTransactionTypesRequired.value && !hasSelectedTransactionTypes.value) {
    return 'Mindestens eine Vorgangsart muss ausgewählt werden.'
  }
  return undefined
})

type TransactionLockScope = 'domestic' | 'foreign'

// Per address scope: the Vorgang options and their texts; the template renders one card per entry.
const transactionLockScopes = [
  {
    key: 'domestic',
    title: 'Inländische Vorgänge (Deutschland)',
    subtitle: 'Vorgänge mit deutscher Adresse',
    lockTransactionField: 'transactionLockDomesticTransaction',
    notificationField: 'domesticTransactionLockNotification',
    notificationDescription: 'E-Mail, wenn bei der Prüfung eines inländischen Vorgangs gesperrt wird. Die E-Mail nennt den Vorgang.',
  },
  {
    key: 'foreign',
    title: 'Ausländische Vorgänge (EU-Ausland)',
    subtitle: 'Vorgänge mit Adresse im EU-Ausland',
    lockTransactionField: 'transactionLockForeignTransaction',
    notificationField: 'foreignTransactionLockNotification',
    notificationDescription: 'E-Mail, wenn bei der Prüfung eines Vorgangs im EU-Ausland gesperrt wird. Die E-Mail nennt den Vorgang.',
  },
] as const

const transactionLocksAddress = (scope: TransactionLockScope): boolean => {
  const vat = config.value.vatValidation
  const flag = scope === 'domestic' ? vat?.transactionLockDomesticAddress : vat?.transactionLockForeignAddress
  return flag ?? true
}

const setTransactionLocksAddress = (scope: TransactionLockScope, value: boolean) => {
  const vat = config.value.vatValidation
  if (!vat) return
  if (scope === 'domestic') {
    vat.transactionLockDomesticAddress = value
  } else {
    vat.transactionLockForeignAddress = value
  }
  emit('dirty')
}

// First invalid notification setting, prefixed with its category. Vorgang notifications are only checked while the
// transaction check is active, since their fields are disabled otherwise.
const lockNotificationValidationError = computed(() => {
  const vat = config.value.vatValidation
  if (!vat) return undefined
  const checks: [string, VatLockNotification][] = [
    ['Inländische Adressen', vat.domesticLockNotification],
    ['Ausländische Adressen', vat.foreignLockNotification],
  ]
  if (isTransactionCheckActive.value) {
    checks.push(
      ['Inländische Vorgänge', vat.domesticTransactionLockNotification],
      ['Ausländische Vorgänge', vat.foreignTransactionLockNotification],
    )
  }
  for (const [label, notification] of checks) {
    const error = lockNotificationError(normalizeLockNotification(notification))
    if (error) return `${label}: ${error}`
  }
  return undefined
})

const onTransactionTriggerChange = () => {
  if (config.value.vatValidation) {
    config.value.vatValidation.transactionCheckActive = isTransactionCheckActive.value
  }
  emit('dirty')
}

const batchValidationRunning = ref(false)
const batchValidationScope = ref<'domestic' | 'foreign' | 'all'>('all')

const onStartBatchValidation = async (scope: 'domestic' | 'foreign' | 'all' = 'all') => {
  if (!props.canWrite || !config.value.vatValidation?.active) return
  batchValidationRunning.value = true
  batchValidationScope.value = scope
  try {
    const res = await vatValidationApi.triggerBatchValidation(
      scope,
      props.tenantName,
      props.customerNo,
      true,
    )
    toast.add({
      title: 'Stapelprüfung gestartet',
      description:
        res.data?.message ||
        'Die Stapelprüfung läuft im Hintergrund. Die Ergebnisse erscheinen nach und nach bei den Adressen.',
      color: 'success',
    })
  } catch (error: unknown) {
    const err = error as { response?: { data?: { details?: string; error?: string } }; message?: string }
    toast.add({
      title: 'Fehler bei der Stapelprüfung',
      description:
        err?.response?.data?.details ||
        err?.response?.data?.error ||
        err?.message ||
        'Die Stapelprüfung konnte nicht ausgeführt werden.',
      color: 'danger',
    })
  } finally {
    batchValidationRunning.value = false
  }
}

defineExpose({
  saveConfig: () => saveConfig(),
  fetchTransactionTypes,
  availableTransactionTypes,
  transactionTypeOptions,
  loadingTransactionTypes,
  isTransactionCheckActive,
  isTransactionTypesRequired,
  hasSelectedTransactionTypes,
  transactionTypesError,
  onTransactionTriggerChange,
  transactionLocksAddress,
  setTransactionLocksAddress,
  lockNotificationValidationError,
  batchValidationRunning,
  batchValidationScope,
  onStartBatchValidation,
})

const fetchConfig = async () => {
  loading.value = true
  try {
    const response = await axios.get<MicrotechConfig>('/api/microtech/v1/config')
    if (response.data) {
      const vatVal = response.data.vatValidation
        ? { ...defaultVatValidation, ...response.data.vatValidation }
        : { ...defaultVatValidation }
      vatVal.domesticLockNotification = normalizeLockNotification(vatVal.domesticLockNotification)
      vatVal.foreignLockNotification = normalizeLockNotification(vatVal.foreignLockNotification)
      vatVal.domesticTransactionLockNotification = normalizeLockNotification(vatVal.domesticTransactionLockNotification)
      vatVal.foreignTransactionLockNotification = normalizeLockNotification(vatVal.foreignTransactionLockNotification)
      if (!Array.isArray(vatVal.transactionTypes)) {
        vatVal.transactionTypes = defaultVatValidation.transactionTypes
          ? [...defaultVatValidation.transactionTypes]
          : []
      }
      if (response.data.vatValidation && response.data.vatValidation.transactionCheckActive === false) {
        vatVal.checkOnTransactionCreate = false
        vatVal.checkOnTransactionUpdate = false
      }
      vatVal.transactionCheckActive = Boolean(
        vatVal.checkOnTransactionCreate || vatVal.checkOnTransactionUpdate
      )
      config.value = {
        ...config.value,
        ...response.data,
        vatValidation: vatVal,
      }
      if (config.value.active) {
        fetchTransactionTypes()
      }
    }
  } catch (error) {
    console.error('Failed to fetch microtech config', error)
    toast.add({
      title: 'Fehler',
      description: 'Fehler beim Laden der Konfiguration.',
      color: 'danger',
    })
  } finally {
    loading.value = false
  }
}

const saveConfig = async (force = false) => {
  if (transactionTypesError.value) {
    toast.add({
      title: 'Validierungsfehler',
      description: transactionTypesError.value,
      color: 'danger',
    })
    return false
  }
  const notificationError = lockNotificationValidationError.value
  if (notificationError) {
    toast.add({
      title: 'Validierungsfehler',
      description: notificationError,
      color: 'danger',
    })
    return false
  }
  if (!force && config.value.active && testResult.value?.success === false) {
    isConfirmSaveModalOpen.value = true
    return false
  }
  saving.value = true
  try {
    if (config.value.vatValidation) {
      config.value.vatValidation.transactionCheckActive = isTransactionCheckActive.value
    }
    // Centrally managed tenants may only change their own activation, indexing, and VAT validation flags.
    const payload = isCentrallyManaged.value
      ? {
          active: config.value.active,
          indexingEnabled: config.value.indexingEnabled,
          vatValidation: config.value.vatValidation,
        }
      : config.value
    await axios.put('/api/microtech/v1/config', payload)
    toast.add({ title: 'Erfolg', description: 'Konfiguration gespeichert.', color: 'success' })
    await fetchConfig()
    return true
  } catch (error) {
    console.error('Failed to save microtech config', error)
    toast.add({
      title: 'Fehler',
      description: 'Fehler beim Speichern der Konfiguration.',
      color: 'danger',
    })
    return false
  } finally {
    saving.value = false
  }
}

const onActiveToggle = () => {
  isConfirmModalOpen.value = true
}

const confirmToggle = () => {
  config.value.active = !config.value.active
  isConfirmModalOpen.value = false
}

const confirmSave = () => {
  isConfirmSaveModalOpen.value = false
  saveConfig(true)
}

const testCredentials = async () => {
  testingCredentials.value = true
  testResult.value = null
  try {
    const response = await axios.post<TestResult>('/api/microtech/v1/config/test', {
      clientId: config.value.clientId || '',
      clientSecret: config.value.clientSecret || '',
      graphqlUrl: config.value.graphUrl || '',
      certificate: config.value.certificate || '',
    })
    testResult.value = {
      success: response.data.success,
      message: response.data.message,
      checks: response.data.checks,
    }
  } catch (error: unknown) {
    let msg = 'Unknown error'
    let checks: TestCheck[] | undefined = undefined
    if (axios.isAxiosError(error)) {
      if (error.response?.data && typeof error.response.data === 'object') {
        msg = error.response.data.message || error.message
        checks = error.response.data.checks
      } else {
        msg = error.response?.data || error.message
      }
    } else if (error instanceof Error) {
      msg = error.message
    }
    testResult.value = { success: false, message: `Fehler: ${msg}`, checks }
  } finally {
    testingCredentials.value = false
  }
}

watch(
  config,
  () => {
    emit('dirty')
  },
  { deep: true },
)

onMounted(() => {
  fetchConfig()
})
</script>
